import api from '../httpClient';
import apiRoutes from '../apiRoutes';

/**
 * Fetch listings belonging to the current authenticated user
 */
export const fetchPropertyListings = async () => {
  const response = await api.get(apiRoutes.listings.list);
  const data = response.data;
  return Array.isArray(data) ? data : (data.results || []);
};

/**
 * Fetch reservation requests for a specific listing (owner view)
 * Uses the new dealbridge API, filtering by listing on the client side.
 */
export const fetchListingReservations = async (listingId) => {
  const response = await api.get(apiRoutes.dealbridge.reservations, { params: { scope: 'received' } });
  const data = response.data;
  const all = Array.isArray(data) ? data : (data.results || []);
  return all.filter(r => r.listing === listingId || r.listing_info?.id === listingId);
};

/**
 * Perform an action on a reservation (accept, reject, cancel)
 */
export const reservationAction = async (reservationId, action, body = {}) => {
  const response = await api.post(apiRoutes.dealbridge.reservationAction(reservationId, action), body);
  return response.data;
};

/**
 * Fetch the offer for a reservation (new API: offers are standalone, filtered by reservation)
 * Returns a single offer object or null.
 */
export const fetchReservationOffer = async (reservationId) => {
  const response = await api.get(apiRoutes.dealbridge.offers);
  const data = response.data;
  const all = Array.isArray(data) ? data : (data.results || []);
  return all.find(o => o.reservation === reservationId) || null;
};

/**
 * Request changes on current revision (counter-offer from the receiving party)
 */
export const requestOfferRevisionChanges = async (revisionId, payload) => {
  const response = await api.post(apiRoutes.dealbridge.offerRevisionAction(revisionId, 'request-changes'), payload);
  return response.data;
};

/**
 * Withdraw an offer revision
 */
export const withdrawOfferRevision = async (revisionId) => {
  const response = await api.post(apiRoutes.dealbridge.offerRevisionAction(revisionId, 'withdraw'), {});
  return response.data;
};

/**
 * Create an offer for a reservation (new API)
 */
export const createOffer = async (payload) => {
  const response = await api.post(apiRoutes.dealbridge.offers, payload);
  return response.data;
};

/**
 * Perform an action on an offer revision (accept, reject, request-changes, withdraw)
 * revisionId is the UUID of the OfferRevision.
 */
export const offerRevisionAction = async (revisionId, action, body = {}) => {
  const response = await api.post(apiRoutes.dealbridge.offerRevisionAction(revisionId, action), body);
  return response.data;
};

/**
 * Fetch visiting schedules for a listing
 */
export const fetchListingSchedules = async (listingId) => {
  const response = await api.get(apiRoutes.listings.schedules(listingId));
  const data = response.data;
  return Array.isArray(data) ? data : (data.results || []);
};

/**
 * Add a visiting schedule slot
 */
export const addScheduleSlot = async (listingId, slot) => {
  const response = await api.post(apiRoutes.listings.schedules(listingId), slot);
  return response.data;
};

/**
 * Delete a visiting schedule slot
 */
export const deleteScheduleSlot = async (listingId, slotId) => {
  const response = await api.delete(apiRoutes.listings.schedule(listingId, slotId));
  return response.data;
};

/**
 * Update listing sale details
 */
export const updateSaleDetails = async (listingId, payload) => {
  const response = await api.patch(apiRoutes.listings.saleDetails(listingId), payload);
  return response.data;
};

/**
 * Update listing rent details
 */
export const updateRentDetails = async (listingId, payload) => {
  const response = await api.patch(apiRoutes.listings.rentDetails(listingId), payload);
  return response.data;
};

/**
 * Fetch single listing by ID
 */
export const fetchListingById = async (listingId) => {

  const response = await api.get(
    apiRoutes.listings.detail(listingId)
  );

  return response.data;
};

/**
 * Create new listing (agent/owner posting)
 */
export const createListing = async (payload) => {

  const response = await api.post(
    apiRoutes.listings.list,
    payload
  );

  return response.data;
};

/**
 * Update existing listing
 */
export const updateListing = async (
  listingId,
  payload
) => {

  const response = await api.patch(
    apiRoutes.listings.detail(listingId),
    payload
  );

  return response.data;
};

/**
 * Delete listing
 */
export const deleteListing = async (listingId) => {

  const response = await api.delete(
    apiRoutes.listings.detail(listingId)
  );

  return response.data;
};

/**
 * Search listings with filters
 * (GIS + AI matching ready endpoint)
 */
export const searchListings = async (filters) => {

  const response = await api.get(apiRoutes.listings.search, {
    params: filters
  });

  return response.data;
};

/**
 * Get recommended listings for user (AI-based)
 */
export const fetchRecommendedListings = async () => {

  const response = await api.get(
    apiRoutes.listings.recommendations
  );

  return response.data;
};
